// src/pages/trading-dashboard/components/BetEntry.jsx
import React, { useState, useEffect } from 'react';
import Icon from 'components/AppIcon';
import { placeBet } from '../../../utils/api';

const BetEntry = ({ selectedMarket, isLoading }) => {
  const [betType, setBetType] = useState('market');
  const [selectedOutcome, setSelectedOutcome] = useState('Yes');
  const [betAmount, setBetAmount] = useState('');
  const [limitPrice, setLimitPrice] = useState('');
  const [isPlacingBet, setIsPlacingBet] = useState(false);
  const [betError, setBetError] = useState('');
  const [betSuccess, setBetSuccess] = useState('');

  // Reset form when market changes
  useEffect(() => {
    setBetAmount('');
    setLimitPrice('');
    setBetError('');
    setBetSuccess('');
    if (selectedMarket?.outcomeTokens?.length > 0) {
      setSelectedOutcome(selectedMarket?.outcomeTokens?.[0]?.name);
    }
  }, [selectedMarket]);

  const getCurrentPrice = () => {
    if (!selectedMarket?.outcomeTokens) return 0;
    const outcome = selectedMarket?.outcomeTokens?.find(t => t?.name === selectedOutcome);
    return outcome?.price || 0;
  };

  const getEstimatedShares = () => {
    if (!betAmount || isNaN(betAmount)) return 0;
    const price = betType === 'market' ? getCurrentPrice() : (parseFloat(limitPrice) || getCurrentPrice());
    return Math.floor(parseFloat(betAmount) / price);
  };

  const getMaxReturn = () => {
    if (!betAmount || isNaN(betAmount)) return 0;
    const shares = getEstimatedShares();
    return shares * 1.00; // Max return is shares * $1 if correct
  };

  const handlePlaceBet = async () => {
    setBetError('');
    setBetSuccess('');

    // Validation
    if (!selectedMarket) {
      setBetError('No market selected');
      return;
    }

    if (!betAmount || parseFloat(betAmount) < 1) {
      setBetError('Minimum bet amount is $1');
      return;
    }

    if (betType === 'limit') {
      if (!limitPrice || parseFloat(limitPrice) < 0.01 || parseFloat(limitPrice) > 0.99) {
        setBetError('Limit price must be between $0.01 and $0.99');
        return;
      }
    }

    setIsPlacingBet(true);

    try {
      const price = betType === 'market' ? getCurrentPrice() : parseFloat(limitPrice);
      const response = await placeBet(selectedMarket?.id, selectedOutcome, parseFloat(betAmount), price);

      if (response?.success) {
        setBetSuccess(`Bet placed successfully! Bet ID: ${response?.data?.betId}`);
        setBetAmount('');
        setLimitPrice('');
      } else {
        setBetError(response?.error || 'Failed to place bet');
      }
    } catch (error) {
      setBetError('Network error. Please try again.');
    } finally {
      setIsPlacingBet(false);
    }
  };

  const quickAmounts = [10, 25, 50, 100];

  if (!selectedMarket) {
    return (
      <div className="p-6">
        <div className="text-center">
          <Icon name="TrendingUp" size={48} className="text-text-secondary mx-auto mb-3" />
          <h3 className="font-body-semibold text-text-primary mb-2">Select a Market</h3>
          <p className="text-sm text-text-secondary">
            Choose a prediction market to start betting
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-heading-semibold text-text-primary">Place Bet</h3>
        <div className="flex items-center space-x-1">
          <div className="w-2 h-2 bg-success rounded-full"></div>
          <span className="text-xs text-text-secondary">Active</span>
        </div>
      </div>
      {/* Bet Type Selection */}
      <div className="mb-6">
        <div className="flex items-center space-x-1 bg-surface-hover rounded-trading p-1">
          <button
            onClick={() => setBetType('market')}
            className={`flex-1 py-2 px-3 text-sm font-body-medium rounded-trading transition-all duration-trading-fast ${
              betType === 'market' ?'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Market
          </button>
          <button
            onClick={() => setBetType('limit')}
            className={`flex-1 py-2 px-3 text-sm font-body-medium rounded-trading transition-all duration-trading-fast ${
              betType === 'limit' ?'bg-primary text-white shadow-sm' :'text-text-secondary hover:text-text-primary'
            }`}
          >
            Limit
          </button>
        </div>
      </div>
      {/* Outcome Selection */}
      <div className="mb-6">
        <label className="block text-sm font-body-medium text-text-primary mb-2">
          Outcome
        </label>
        <div className="space-y-2">
          {selectedMarket?.outcomeTokens?.map((token, index) => (
            <button
              key={index}
              onClick={() => setSelectedOutcome(token?.name)}
              className={`w-full flex items-center justify-between p-3 rounded-trading border transition-all duration-trading-fast ${
                selectedOutcome === token?.name
                  ? 'border-primary bg-primary-900 bg-opacity-20' :'border-border hover:border-border-hover hover:bg-surface-hover'
              }`}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-4 h-4 rounded-full border-2 ${
                  selectedOutcome === token?.name
                    ? 'border-primary bg-primary' :'border-border'
                }`}>
                  {selectedOutcome === token?.name && (
                    <div className="w-full h-full rounded-full bg-white transform scale-50"></div>
                  )}
                </div>
                <span className="font-body-medium text-text-primary">{token?.name}</span>
              </div>
              <div className="text-right">
                <p className="font-data text-primary">${token?.price?.toFixed(2)}</p>
                <p className="text-xs text-text-secondary">{token?.shares?.toLocaleString()} shares</p>
              </div>
            </button>
          ))}
        </div>
      </div>
      {/* Bet Amount */}
      <div className="mb-4">
        <label className="block text-sm font-body-medium text-text-primary mb-2">
          Bet Amount (USD)
        </label>
        <div className="relative">
          <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary">$</span>
          <input
            type="number"
            value={betAmount}
            onChange={(e) => setBetAmount(e?.target?.value)}
            placeholder="0.00"
            min="1"
            step="0.01"
            className="w-full pl-8 pr-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
          />
        </div>
        
        {/* Quick Amount Buttons */}
        <div className="flex items-center space-x-2 mt-2">
          {quickAmounts?.map(amount => (
            <button
              key={amount}
              onClick={() => setBetAmount(amount?.toString())}
              className="px-3 py-1 text-xs bg-surface-hover text-text-secondary rounded hover:bg-border-hover hover:text-text-primary transition-colors duration-trading-fast"
            >
              ${amount}
            </button>
          ))}
        </div>
      </div>
      {/* Limit Price (if limit order) */}
      {betType === 'limit' && (
        <div className="mb-4">
          <label className="block text-sm font-body-medium text-text-primary mb-2">
            Limit Price
          </label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary">$</span>
            <input
              type="number"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e?.target?.value)}
              placeholder={getCurrentPrice()?.toFixed(2)}
              min="0.01"
              max="0.99"
              step="0.01"
              className="w-full pl-8 pr-4 py-3 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary bg-background text-text-primary"
            />
          </div>
        </div>
      )}
      {/* Bet Summary */}
      <div className="mb-6 p-4 bg-surface-hover rounded-trading border border-border">
        <h4 className="font-body-semibold text-text-primary mb-3">Bet Summary</h4>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-text-secondary">Estimated Shares:</span>
            <span className="text-text-primary font-data">{getEstimatedShares()?.toLocaleString()}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Max Return:</span>
            <span className="text-success font-data">${getMaxReturn()?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-text-secondary">Potential Profit:</span>
            <span className="text-success font-data">
              ${(getMaxReturn() - parseFloat(betAmount || 0))?.toFixed(2)}
            </span>
          </div>
        </div>
      </div>
      {/* Error/Success Messages */}
      {betError && (
        <div className="mb-4 p-3 bg-error-900 bg-opacity-20 border border-error-600 rounded-trading">
          <p className="text-error-300 text-sm">{betError}</p>
        </div>
      )}
      {betSuccess && (
        <div className="mb-4 p-3 bg-success-900 bg-opacity-20 border border-success-600 rounded-trading">
          <p className="text-success-300 text-sm">{betSuccess}</p>
        </div>
      )}
      {/* Place Bet Button */}
      <button
        onClick={handlePlaceBet}
        disabled={isPlacingBet || !betAmount || isLoading}
        className="w-full bg-gradient-primary text-white py-3 rounded-trading font-body-semibold hover:opacity-90 transition-opacity duration-trading-fast disabled:opacity-50 disabled:cursor-not-allowed glow-primary"
      >
        {isPlacingBet ? (
          <div className="flex items-center justify-center space-x-2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            <span>Placing Bet...</span>
          </div>
        ) : (
          `Place ${betType?.charAt(0)?.toUpperCase() + betType?.slice(1)} Bet`
        )}
      </button>
      <p className="text-xs text-text-secondary mt-2 text-center">
        By placing a bet, you agree to our Terms of Service and acknowledge the risks involved in prediction markets.
      </p>
    </div>
  );
};

export default BetEntry;